import React, { useEffect, useRef, useState } from "react";

import { Field } from "components/ui";
import SaveBar from "features/account/SaveBar";
import useSaveProfile from "features/account/useSaveProfile";
import { OCCUPATIONS, professionOf } from "features/account/profile";
import { readAsDataUrl } from "lib/file";

function CameraIcon() {
  return (
    <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
      <path d="M7 4l1.2-1.5h3.6L13 4h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3z" fill="currentColor" />
      <circle cx="10" cy="9.5" r="3" fill="#232933" />
    </svg>
  );
}

// "My profile": avatar column on the left, details on the right.
export default function ProfileForm() {
  const { details, errors, status, save, reset } = useSaveProfile();
  const fileInput = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", profession: "", otherProfession: "", avatar: "" });

  // The profile loads asynchronously; fill the form once it arrives.
  useEffect(() => {
    if (!details?.id) return;
    const known = OCCUPATIONS.some(([v]) => v && v === details.profession);
    setForm({
      name: details.name ?? "",
      email: details.email ?? "",
      avatar: details.avatar ?? "",
      profession: !details.profession ? "" : known ? details.profession : "others",
      otherProfession: known ? "" : details.profession ?? "",
    });
  }, [details]);

  const set = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    reset();
  };

  function pickPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    readAsDataUrl(file).then((avatar) => {
      setForm((prev) => ({ ...prev, avatar }));
      reset();
    });
  }

  async function submit(e) {
    e.preventDefault();
    const saved = await save({ name: form.name.trim(), email: form.email.trim(), profession: professionOf(form) }, { avatar: form.avatar });
    if (saved?.avatar) setForm((prev) => ({ ...prev, avatar: saved.avatar }));
  }

  return (
    <form onSubmit={submit} noValidate className="yc-profile">
      <div className="yc-profile-photo">
        <button type="button" className="yc-profile-avatar" onClick={() => fileInput.current.click()} aria-label="Upload a new photo">
          {form.avatar ? <img src={form.avatar} alt="" /> : <span>{(form.name || "?").trim().charAt(0).toUpperCase()}</span>}
          <span className="yc-profile-camera">
            <CameraIcon />
          </span>
        </button>
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={pickPhoto} hidden />
        <b>{form.name}</b>
        <button type="button" className="yc-link" onClick={() => fileInput.current.click()}>
          Upload new photo
        </button>
      </div>

      <div className="yc-profile-fields">
        <Field label="Full name" name="name" value={form.name} onChange={set} error={errors.name} autoComplete="name" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={set} error={errors.email} autoComplete="email" />
        <Field label="Occupation" name="profession" value={form.profession} onChange={set} options={OCCUPATIONS} error={errors.profession} />
        {form.profession === "others" && (
          <Field label="Your occupation" name="otherProfession" value={form.otherProfession} onChange={set} placeholder="e.g. Marketing Executive" />
        )}
        <SaveBar status={status} error={errors.form} />
      </div>
    </form>
  );
}
